import React, { useMemo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreValue } from 'store';
import { LayoutPage, LayoutContent, FormRenderer, Form, SecureIcon } from 'components';
import { initFormConfig, submitHandler, isAU, byCountry, isDJ, isNZ } from 'utils';
import { useSetUserCredentials, useSteps, useDataLayerAndAnalyticsLinks } from 'hooks';
import { DEV_FEATURES } from '__dev/devFeatures';
import { STEP_PROCESSING } from '_config';
import styled from '@emotion/styled';
import { isFeatureOn } from 'featureToggles';
import { Password, Grid, Button } from 'basis';

const LatitudeIDWrapper = styled.div`
  & input[name='latitudeId'] {
    padding-left: ${isAU() ? 0 : 'auto'};
  }

  ${!isDJ() &&
    `& button[type='submit'] {
    background-color: #20df5c;
    color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
    font-family: var(--Family-font-brand, 'Latitude Sans');
    font-weight: 600;
    
    &:hover:not(:disabled) {
      color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
      background-color: var(--Background-Primary-hover, #6cf898);
      
    }
  }`}
`;

const IconAndText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & svg {
    margin-right: 8px;
    margin-top: -2px;
  }
`;

const NextButton = ({ valid }) => {
  return (
    <Grid
      colsGap="10"
      cols={byCountry({
        AU: '2',
        NZ: '2',
      })}
    >
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <Grid>
          <Button testId="nextBtn" type="submit" disabled={!valid}>
            {(isAU() || isNZ() || isDJ()) && (
              <IconAndText>
                <SecureIcon color={valid ? '#0B2374' : '#414141'} />
                Set up now
              </IconAndText>
            )}
            {!(isAU() || isNZ()) && 'Next'}
          </Button>
        </Grid>
      </Grid.Item>
    </Grid>
  );
};

export function LatitudeId() {
  const [storeState, updateStore] = useStoreValue();
  const { acquisition } = storeState;
  const { nextStep } = useSteps();
  const history = useHistory();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const setUserCredentials = useSetUserCredentials();
  const noProgressStepper = isAU();
  const [isPasswordValid, setPasswordValid] = useState(false);
  const next = useCallback(() => {
    const step = byCountry({
      NZ: STEP_PROCESSING,
      AU: isFeatureOn('emailVerification') ? STEP_PROCESSING : nextStep,
    });
    updateStore({
      applicationErrors: null,
      isLatitudeIdPasswordSet: true,
      activeStep: step,
    });

    history.push(step);
  }, [history, nextStep, updateStore]);

  const onSubmit = ({ values, errors, setErrors }) => {
    async function setAndGoNext() {
      trackEventSender({
        category: 'application',
        action: 'application-navigation',
        location: 'latitude-id',
        label: byCountry({
          AU: 'Set up now',
          NZ: 'Set up now',
        }),
      });

      if (DEV_FEATURES.OTP_SKIP) {
        next();
      }
      const { error } = await setUserCredentials(values.password);
      const validationErrorMsg = error?.response?.data?.message?.split('password:')[1];
      if (error) {
        setErrors({ password: validationErrorMsg || error.message });
      } else {
        next();
      }
    }

    submitHandler({ submit: setAndGoNext, errors });
  };

  const initialValues = useMemo(
    () => ({
      latitudeId: acquisition.contactDetails.emailAddress,
      password: '',
    }),
    [acquisition],
  );

  const formConfig = initFormConfig([
    {
      component: 'InfoBlock',
      name: 'info',
      text: byCountry({
        NZ: `Create the password you'd like to use for the Latitude App and Latitude Service Centre.`,
        AU: isFeatureOn('emailVerification')
          ? `Create the password you'd like to use for the Latitude App and Latitude Service Centre.`
          : 'A Latitude ID gives you access to both our Mobile App and Online Latitude Service Centre.',
      }),
    },
    {
      component: 'Input',
      name: 'latitudeId',
      label: byCountry({
        AU: isFeatureOn('emailVerification') ? 'Your email address' : 'Latitude ID username',
        NZ: 'Your email address',
      }),
      testId: 'latitudeIdField',
      color: isAU() ? 'white' : null,
      disabled: true,
      border: 'none',
    },
    {
      component: 'CustomComponent',
      children: Password,
      name: 'password',
      label: 'Create a password',
      testId: 'password',
      onValidityChange: setPasswordValid,
    },
  ]);

  return (
    <LayoutPage noProgressStepper={noProgressStepper}>
      <Form id="latitudeId" initialValues={initialValues} onSubmit={onSubmit} hideFormButtons disableFormCache>
        {formData => {
          return (
            <LatitudeIDWrapper>
              <LayoutContent>
                <FormRenderer config={formConfig} formData={formData} />
                <NextButton valid={isPasswordValid} />
              </LayoutContent>
            </LatitudeIDWrapper>
          );
        }}
      </Form>
    </LayoutPage>
  );
}
